// ═══════════════════════════════════════════════════════════
//  NETWORK CONFIGURATION - Change ACTIVE_NETWORK to switch
// ═══════════════════════════════════════════════════════════
export const ACTIVE_NETWORK = "mainnet"; // "mainnet" or "testnet"

const networks = {
  mainnet: {
    chainId: 204,
    chainIdHex: "0xCC",
    chainName: "opBNB Mainnet",
    rpcUrl: "https://opbnb-mainnet-rpc.bnbchain.org",
    blockExplorer: "https://opbnbscan.com",
    nativeCurrency: { name: "BNB", symbol: "BNB", decimals: 18 },
    // Per-contract deploy blocks - event scans start from each contract's own block
    deployBlocks: {
      data: 137147024,     // Data contract (oldest, kept across plan redeploys)
      plan1: 150960039,    // Plan1 — set to block of current deployment
      plan2: 150960285,    // Plan2 — set to block of current deployment
    },
    contracts: {
      token: "0x9e5AAC1Ba1a2e6aEd6b32689DFcF62A509Ca96f3", // USDT on opBNB mainnet
      data: "0x97591140E29a7B67589417aA76112B3f387Abb5D",   // Deploy and fill
      plan1: "0x39879363015138bfBFAA0347730193ad49A49c09",  // Deploy and fill
      plan2: "0xbaCC37043866C60b5f66351CE3D518645AEbb87A",  // Deploy and fill
    },
  },
  testnet: {
    chainId: 5611,
    chainIdHex: "0x15EB",
    chainName: "opBNB Testnet",
    rpcUrl: "https://opbnb-testnet-rpc.bnbchain.org",
    blockExplorer: "https://testnet.opbnbscan.com",
    nativeCurrency: { name: "tBNB", symbol: "tBNB", decimals: 18 },
    deployBlocks: {
      data: 0,
      plan1: 0,
      plan2: 0,
    },
    contracts: {
      token: "0xBf6596a279172D330b8b732D257Ee633B6FDA911",  // Testnet USDT - deploy and fill
      data: "0xf50D5671B6f16bD71273fDA47371b8CBAc7C4368",   // Deploy and fill
      plan1: "0x301d02880B95568755Fa3358C6ee06B7f210262a",  // Deploy and fill
      plan2: "0xCE86E24468502A6Cb9532Ccb8e1B0EC0b7708eA5",  // Deploy and fill
    },
  },
};

export const NETWORK = networks[ACTIVE_NETWORK];
export const TOKEN_DECIMALS = 18;
export const TOKEN_SYMBOL = "USDT";
export const PLAN1_PKG_COUNT = 8;
export const PLAN2_PKG_COUNT = 8;
export const PLAN1_MATRIX_LEVELS = 8;
export const PLAN2_MATRIX_LEVELS = 10;

// ═══════════════════════════════════════════════════════════
//  ABIs
// ═══════════════════════════════════════════════════════════

export const TOKEN_ABI = [
  "function approve(address spender, uint256 amount) external returns (bool)",
  "function allowance(address owner, address spender) external view returns (uint256)",
  "function balanceOf(address account) external view returns (uint256)",
  "function transfer(address to, uint256 value) external returns (bool)",
  "function transferFrom(address from, address to, uint256 value) external returns (bool)",
  "function decimals() external view returns (uint8)",
  "function symbol() external view returns (string)",
];

export const DATA_ABI = [
  // Registration
  "function register(uint256 refId, string calldata name) external",
  "function registerForUser(address wallet, uint256 refId, string calldata name) external",
  "function virtualRegister(address w, uint256 refId, string calldata name) external",
  // Claim
  "function claim() external",
  "function claimAmount(uint256 amt) external",
  "function emergencyWithdraw(uint256 amt) external",
  // Admin setters
  "function setPlan1Contract(address p) external",
  "function setPlan2Contract(address p) external",
  "function setAdmin(address a) external",
  "function transferOwnership(address o) external",
  "function setAdminWallet(address w) external",
  "function setDeductionWallet(address w) external",
  "function setMissingIncomeWallet(address w) external",
  "function setRouter1(address w) external",
  "function setRouter2(address w) external",
  "function setRouter1Enabled(bool e) external",
  "function setRouter2Enabled(bool e) external",
  "function setDefaultReferrer(uint256 uid) external",
  "function blacklistUser(uint256 uid, bool status) external",
  // Read
  "function owner() external view returns (address)",
  "function admin() external view returns (address)",
  "function plan1Contract() external view returns (address)",
  "function plan2Contract() external view returns (address)",
  "function token() external view returns (address)",
  "function adminWallet() external view returns (address)",
  "function deductionWallet() external view returns (address)",
  "function missingIncomeWallet() external view returns (address)",
  "function router1Wallet() external view returns (address)",
  "function router2Wallet() external view returns (address)",
  "function router1Enabled() external view returns (bool)",
  "function router2Enabled() external view returns (bool)",
  "function defaultReferrerId() external view returns (uint256)",
  "function totalUsers() external view returns (uint256)",
  "function addrToId(address) external view returns (uint256)",
  "function claimableBalance(uint256) external view returns (uint256)",
  "function blacklisted(uint256) external view returns (bool)",
  "function isRegistered(uint256 uid) external view returns (bool)",
  "function isBlacklisted(uint256 uid) external view returns (bool)",
  "function getUserWallet(uint256 uid) external view returns (address)",
  "function getUserReferrer(uint256 uid) external view returns (uint256)",
  "function getUserDirectCount(uint256 uid) external view returns (uint256)",
  "function getIdByAddress(address addr) external view returns (uint256)",
  // View (frontend)
  "function getUserInfo(uint256 uid) external view returns (address wallet, uint256 id, uint256 referrerId, string name, uint256 totalInvested, uint256 totalEarned, uint256 totalClaimed, uint256 claimable, uint256 directRefCount, bool active, bool isBL)",
  "function getDirectRefs(uint256 uid, uint256 page, uint256 size) external view returns (uint256[] ids, uint256 total)",
  "function getDirectRefDetails(uint256 upId, uint256 page, uint256 size) external view returns (uint256[] ids, address[] wallets, uint256[] invested, bool[] active_, uint256 total)",
  "function getActivity(uint256 uid, uint256 page, uint256 size) external view returns (tuple(uint256 timestamp, uint256 amount, uint8 actType, uint8 plan, uint8 level, uint256 fromUserId)[] acts, uint256 total)",
  "function getGlobalStats() external view returns (uint256, uint256)",
  "function getRouterStatus() external view returns (bool, bool, address, address)",
  "function getContractBalance() external view returns (uint256)",
  "function getAllUsers(uint256 page, uint256 size) external view returns (uint256[] ids, uint256 total)",
  "function getAllUsersNewest(uint256 page, uint256 size) external view returns (uint256[] ids, uint256 total)",
  // Events
  "event Registered(address indexed wallet, uint256 indexed userId, uint256 indexed refId, string name)",
  "event Claimed(uint256 indexed userId, uint256 amount)",
];

export const PLAN1_ABI = [
  // Public
  "function activatePackage(uint256 pkg) external",
  "function activateForUser(uint256 targetId, uint256 pkg) external",
  "function claimRoyalty() external",
  "function manualAutoUpgrade(uint256 pkg) external",
  // Admin
  "function virtualActivate(uint256 uid, uint256 pkg) external",
  "function virtualActivateBatch(uint256[] uids, uint256[] pkgs) external",
  "function distributeRoyaltyPool(uint256 amount) external",
  "function withdrawRouterPool(uint256 amount) external",
  "function releaseHeldBatch(uint256[] uids) external",
  "function setAdmin(address a) external",
  "function transferOwnership(address o) external",
  "function setDataContract(address d) external",
  "function setRouter1Pct(uint256 p) external",
  "function setRouter2Pct(uint256 p) external",
  "function setRouterPoolPct(uint256 p) external",
  "function setRouter1Enabled(bool e) external",
  "function setRouter2Enabled(bool e) external",
  "function setRouterPoolEnabled(bool e) external",
  "function setPkgPrice(uint256 i, uint256 p) external",
  "function setAutoUpgradeEnabled(bool e) external",
  "function setAutoUpgradeMaxDepth(uint256 d) external",
  "function setDeductPct(uint256 p) external",
  "function setAdminPct(uint256 p) external",
  "function lockMigration() external",
  // View
  "function owner() external view returns (address)",
  "function admin() external view returns (address)",
  "function packages(uint256) external view returns (uint256)",
  "function directRefPct() external view returns (uint256)",
  "function levelPct() external view returns (uint256)",
  "function adminPct() external view returns (uint256)",
  "function royaltyPct() external view returns (uint256)",
  "function deductPct() external view returns (uint256)",
  "function router1Pct() external view returns (uint256)",
  "function router2Pct() external view returns (uint256)",
  "function routerPoolPct() external view returns (uint256)",
  "function router1Enabled() external view returns (bool)",
  "function router2Enabled() external view returns (bool)",
  "function routerPoolEnabled() external view returns (bool)",
  "function routerPoolBalance() external view returns (uint256)",
  "function royaltyPoolBalance() external view returns (uint256)",
  "function royaltyEligibleCount() external view returns (uint256)",
  "function royaltyEligible(uint256) external view returns (bool)",
  "function autoUpgradeEnabled() external view returns (bool)",
  "function autoUpgradeMaxDepth() external view returns (uint256)",
  "function migrationComplete() external view returns (bool)",
  "function getPackageInfo(uint256 uid, uint256 pkg) external view returns (bool isActive, uint256 baseAmt, uint256 earned, uint256 holdBal)",
  "function getAllPackages(uint256 uid) external view returns (bool[8] a, uint256[8] e, uint256[8] h)",
  "function getIncomeStats(uint256 uid) external view returns (uint256 direct, uint256 lvl, uint256 royalty)",
  "function getPkgIncome(uint256 uid, uint256 pkg) external view returns (uint256 direct, uint256 lvl)",
  "function getPendingRoyalty(uint256 uid) external view returns (uint256)",
  "function getRoyaltyInfo() external view returns (uint256 pool, uint256 round_, uint256 eligible, uint256 dps_)",
  "function getRouterPoolInfo() external view returns (uint256)",
  "function getMatrixInfo(uint256 uid) external view returns (uint256 parent, bool placed, uint256 childCount)",
  "function getMatrixChildren(uint256 uid) external view returns (uint256[])",
  "function getMatrixUplines(uint256 uid) external view returns (uint256[8] uplines)",
  "function getMatrixLevelCounts(uint256 uid) external view returns (uint256[8] counts)",
  "function getMatrixLevelUsers(uint256 uid, uint256 level, uint256 page, uint256 size) external view returns (uint256[] ids, uint256 total)",
  "function getRoyaltyEligibleList(uint256 page, uint256 size) external view returns (uint256[] ids, uint256 total)",
  "function getRoyaltyEligibleCount() external view returns (uint256)",
  "function royaltyEligibleList(uint256) external view returns (uint256)",
  "function royaltyEligibleIndex(uint256) external view returns (uint256)",
  "function getPackagePrices() external view returns (uint256[8])",
  // Events
  "event Activated(uint256 indexed uid, uint256 pkg, uint256 amt)",
  "event MatrixPlaced(uint256 indexed uid, uint256 indexed parentId, uint256 childIndex)",
  "event DirectRefIncome(uint256 indexed toId, uint256 indexed fromId, uint256 pkg, uint256 amt)",
  "event LevelIncome(uint256 indexed toId, uint256 indexed fromId, uint256 lvl, uint256 pkg, uint256 amt)",
  "event AutoUpgrade(uint256 indexed uid, uint256 fromPkg, uint256 toPkg, uint256 amt)",
  "event IncomeHeld(uint256 indexed uid, uint256 pkg, uint256 amt, uint8 incType)",
  "event HeldReleased(uint256 indexed uid, uint256 pkg, uint256 amt)",
  "event MissingIncome(uint256 indexed fromId, uint256 pkg, uint8 incType, uint256 amt)",
  "event UplineAct(uint256 indexed upId, uint256 indexed targetId)",
  "event RouterPoolWithdrawn(address indexed to, uint256 amt)",
  "event RoyaltyDistributed(uint256 amt, uint256 eligible)",
  "event RoyaltyClaimed(uint256 indexed uid, uint256 amt)",
];

export const PLAN2_ABI = [
  // Public
  "function activatePackage(uint256 pkg) external",
  "function activateForUser(uint256 targetId, uint256 pkg) external",
  "function claimGlobalShare(uint256 pkg) external",
  "function claimAllGlobalShares() external",
  "function claimRoyalty() external",
  "function manualAutoUpgrade(uint256 pkg) external",
  // Admin
  "function virtualActivate(uint256 uid, uint256 pkg) external",
  "function virtualActivateBatch(uint256[] uids, uint256[] pkgs) external",
  "function distributeGlobalPool(uint256 pkg, uint256 amount) external",
  "function distributeRoyaltyPool(uint256 amount) external",
  "function withdrawRouterPool(uint256 amount) external",
  "function releaseHeldBatch(uint256[] uids) external",
  "function setAdmin(address a) external",
  "function transferOwnership(address o) external",
  "function setDataContract(address d) external",
  "function setRouter1Pct(uint256 p) external",
  "function setRouter2Pct(uint256 p) external",
  "function setRouterPoolPct(uint256 p) external",
  "function setRouter1Enabled(bool e) external",
  "function setRouter2Enabled(bool e) external",
  "function setRouterPoolEnabled(bool e) external",
  "function setAdminPct(uint256 p) external",
  "function setPkgPrice(uint256 i, uint256 p) external",
  "function setLevelPct(uint256 lvl, uint256 pct) external",
  "function setAllLevelPcts(uint256[10] pcts) external",
  "function setAutoUpgradeEnabled(bool e) external",
  "function setAutoUpgradeMaxDepth(uint256 d) external",
  "function setDeductPct(uint256 p) external",
  "function lockMigration() external",
  // View
  "function owner() external view returns (address)",
  "function admin() external view returns (address)",
  "function packages(uint256) external view returns (uint256)",
  "function globalSharePct() external view returns (uint256)",
  "function directRefPct() external view returns (uint256)",
  "function adminPct() external view returns (uint256)",
  "function royaltyPct() external view returns (uint256)",
  "function deductPct() external view returns (uint256)",
  "function router1Pct() external view returns (uint256)",
  "function router2Pct() external view returns (uint256)",
  "function routerPoolPct() external view returns (uint256)",
  "function router1Enabled() external view returns (bool)",
  "function router2Enabled() external view returns (bool)",
  "function routerPoolEnabled() external view returns (bool)",
  "function routerPoolBalance() external view returns (uint256)",
  "function royaltyPoolBalance() external view returns (uint256)",
  "function royaltyEligibleCount() external view returns (uint256)",
  "function royaltyEligible(uint256) external view returns (bool)",
  "function autoUpgradeEnabled() external view returns (bool)",
  "function autoUpgradeMaxDepth() external view returns (uint256)",
  "function migrationComplete() external view returns (bool)",
  "function globalPool(uint256) external view returns (uint256)",
  "function globalDPS(uint256) external view returns (uint256)",
  "function globalEligibleCount(uint256) external view returns (uint256)",
  "function globalEligible(uint256 uid, uint256 pkg) external view returns (bool)",
  "function getPackageInfo(uint256 uid, uint256 pkg) external view returns (bool isActive, uint256 baseAmt, uint256 earned, uint256 holdBal)",
  "function getAllPackages(uint256 uid) external view returns (bool[8] a, uint256[8] e, uint256[8] h)",
  "function getIncomeStats(uint256 uid) external view returns (uint256 direct, uint256 lvl, uint256 glob, uint256 royalty)",
  "function getPkgIncome(uint256 uid, uint256 pkg) external view returns (uint256 direct, uint256 lvl, uint256 glob)",
  "function getPendingGlobal(uint256 uid, uint256 pkg) external view returns (uint256)",
  "function getAllPendingGlobal(uint256 uid) external view returns (uint256[8] p)",
  "function getGlobalEligibility(uint256 uid) external view returns (bool[8] e)",
  "function getGlobalPoolInfo(uint256 pkg) external view returns (uint256 pool, uint256 dps, uint256 eligible)",
  "function getPendingRoyalty(uint256 uid) external view returns (uint256)",
  "function getRoyaltyInfo() external view returns (uint256 pool, uint256 round_, uint256 eligible, uint256 dps_)",
  "function getRouterPoolInfo() external view returns (uint256)",
  "function getMatrixInfo(uint256 uid, uint256 pkg) external view returns (uint256 parent, bool placed, uint256 childCount)",
  "function getMatrixChildren(uint256 uid, uint256 pkg) external view returns (uint256[])",
  "function getMatrixUplines(uint256 uid, uint256 pkg) external view returns (uint256[10] uplines)",
  "function getMatrixLevelCounts(uint256 uid, uint256 pkg) external view returns (uint256[10] counts)",
  "function getMatrixLevelUsers(uint256 uid, uint256 pkg, uint256 level, uint256 page, uint256 size) external view returns (uint256[] ids, uint256 total)",
  "function getRoyaltyEligibleList(uint256 page, uint256 size) external view returns (uint256[] ids, uint256 total)",
  "function getRoyaltyEligibleCount() external view returns (uint256)",
  "function royaltyEligibleList(uint256) external view returns (uint256)",
  "function royaltyEligibleIndex(uint256) external view returns (uint256)",
  "function getPackagePrices() external view returns (uint256[8])",
  "function getLevelPercents() external view returns (uint256[10])",
  // Events
  "event Activated(uint256 indexed uid, uint256 pkg, uint256 amt)",
  "event MatrixPlaced(uint256 indexed uid, uint256 indexed parentId, uint256 pkg, uint256 childIndex)",
  "event DirectRefIncome(uint256 indexed toId, uint256 indexed fromId, uint256 pkg, uint256 amt)",
  "event LevelIncome(uint256 indexed toId, uint256 indexed fromId, uint256 lvl, uint256 pkg, uint256 amt)",
  "event GlobalShareDistributed(uint256 pkg, uint256 amt, uint256 eligible)",
  "event GlobalShareClaimed(uint256 indexed uid, uint256 pkg, uint256 amt)",
  "event AutoUpgrade(uint256 indexed uid, uint256 fromPkg, uint256 toPkg, uint256 amt)",
  "event IncomeHeld(uint256 indexed uid, uint256 pkg, uint256 amt, uint8 incType)",
  "event HeldReleased(uint256 indexed uid, uint256 pkg, uint256 amt)",
  "event MissingIncome(uint256 indexed fromId, uint256 pkg, uint8 incType, uint256 amt)",
  "event RouterPoolWithdrawn(address indexed to, uint256 amt)",
  "event RoyaltyDistributed(uint256 amt, uint256 eligible)",
  "event RoyaltyClaimed(uint256 indexed uid, uint256 amt)",
];
